import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { School } from 'lucide-react';
import { z } from 'zod';
import { EMPTY_VALUE } from '@/constants/app.constants';
import { Form, FormControl, FormField, FormItem, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { PCMP_EDIT_LIMITS } from '../../constants/pcmpProposal.constants';
import { useEditPcmpProposalSection } from '../../hooks/useEditPcmpProposalSection';
import type { PcmpProposalDetail } from '../../types/pcmpProposal.types';
import { pcmpEditReasonSchema } from '../../utils/pcmpProposalEditForm';
import { buildPcmpStudentGrid } from '../../utils/pcmpStudentGrid';
import { PcmpEditDialog } from './PcmpEditDialog';
import { PcmpEditReasonField } from './PcmpEditReasonField';

const qtySchema = z
  .string()
  .trim()
  .regex(/^\d+$/, 'Whole number')
  .refine((value) => Number(value) <= PCMP_EDIT_LIMITS.maxQty, 'Too large');

const studentFormSchema = z.object({
  classes: z.array(z.object({ classId: z.number(), studentQty: qtySchema })),
  books: z.array(z.object({ projectBookId: z.number(), tentativePurchaseQty: qtySchema })),
  reason: pcmpEditReasonSchema,
});

type StudentFormValues = z.infer<typeof studentFormSchema>;

interface PcmpStudentEditDialogProps {
  proposal: PcmpProposalDetail;
  onClose: () => void;
}

/** Legacy EditStudentInfo: class student counts and book quantities. */
export function PcmpStudentEditDialog({
  proposal: openedWith,
  onClose,
}: PcmpStudentEditDialogProps) {
  // The rows the form was built from: a refetch while open must not shift them.
  const [proposal] = useState(openedWith);
  const { students, calculation } = proposal;
  const grid = useMemo(() => buildPcmpStudentGrid(students, calculation), [students, calculation]);

  const defaultValues = useMemo<StudentFormValues>(
    () => ({
      classes: students.map((row) => ({
        classId: row.classId,
        studentQty: String(row.studentQty),
      })),
      books: students.flatMap((row) =>
        row.books.map((book) => ({
          projectBookId: book.projectBookId,
          tentativePurchaseQty: String(book.tentativePurchaseQty),
        })),
      ),
      reason: '',
    }),
    [students],
  );
  const classIndex = useMemo(
    () => new Map(defaultValues.classes.map((row, index) => [row.classId, index])),
    [defaultValues],
  );
  const bookIndex = useMemo(
    () => new Map(defaultValues.books.map((book, index) => [book.projectBookId, index])),
    [defaultValues],
  );

  const form = useForm<StudentFormValues>({
    resolver: zodResolver(studentFormSchema),
    defaultValues,
  });
  const { mutate, isPending, error } = useEditPcmpProposalSection(
    proposal.proposalId,
    'students',
    onClose,
  );

  // Only the changed rows go out; an untouched grid lets the API answer
  // "Nothing changed".
  const onSubmit = form.handleSubmit((values) => {
    const classes = values.classes
      .filter(
        (row, index) => Number(row.studentQty) !== Number(defaultValues.classes[index]?.studentQty),
      )
      .map((row) => ({ classId: row.classId, studentQty: Number(row.studentQty) }));
    const books = values.books
      .filter(
        (book, index) =>
          Number(book.tentativePurchaseQty) !==
          Number(defaultValues.books[index]?.tentativePurchaseQty),
      )
      .map((book) => ({
        projectBookId: book.projectBookId,
        tentativePurchaseQty: Number(book.tentativePurchaseQty),
      }));
    mutate({
      ...(classes.length > 0 ? { classes } : {}),
      ...(books.length > 0 ? { books } : {}),
      reason: values.reason,
    });
  });

  return (
    <Form {...form}>
      <PcmpEditDialog
        open
        onOpenChange={(open) => !open && onClose()}
        title="Edit Students"
        icon={School}
        capabilities={proposal.capabilities}
        isDirty={form.formState.isDirty}
        isPending={isPending}
        error={error}
        onSubmit={(event) => void onSubmit(event)}
        className="sm:max-w-3xl"
      >
        <p className="text-sm text-muted-foreground">
          Student counts and book quantities belong to the whole submission, so every proposal of
          this school sees the change.
        </p>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Class</TableHead>
                <TableHead className="min-w-28 text-right">Students</TableHead>
                {grid.categories.map((category) => (
                  <TableHead key={category.categoryId} className="min-w-28 text-right">
                    {category.categoryName} qty
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {grid.rows.map((row) => {
                const rowIndex = classIndex.get(row.classId) ?? 0;
                return (
                  <TableRow key={row.classId}>
                    <TableCell className="font-medium">{row.className}</TableCell>
                    <TableCell>
                      <FormField
                        control={form.control}
                        name={`classes.${rowIndex}.studentQty`}
                        render={({ field }) => (
                          <FormItem>
                            <FormControl>
                              <Input
                                {...field}
                                inputMode="numeric"
                                autoComplete="off"
                                aria-label={`${row.className} students`}
                                className="text-right tabular-nums"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </TableCell>
                    {grid.categories.map((category) => {
                      const book = row.booksByCategory.get(category.categoryId);
                      const index = book ? bookIndex.get(book.projectBookId) : undefined;
                      return (
                        <TableCell key={category.categoryId} className="text-right">
                          {index === undefined ? (
                            <span className="text-muted-foreground">{EMPTY_VALUE}</span>
                          ) : (
                            <FormField
                              control={form.control}
                              name={`books.${index}.tentativePurchaseQty`}
                              render={({ field }) => (
                                <FormItem>
                                  <FormControl>
                                    <Input
                                      {...field}
                                      inputMode="numeric"
                                      autoComplete="off"
                                      aria-label={`${row.className} ${category.categoryName} qty`}
                                      className="text-right tabular-nums"
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
        <PcmpEditReasonField control={form.control} />
      </PcmpEditDialog>
    </Form>
  );
}
